import StripeCustomer from "@/types/StripeCustomer.interface"
import StripeInvoice from "@/types/StripeInvoice.interface"

interface State {
    selectedStripeInvoice: StripeInvoice
    stripeCustomer: StripeCustomer
    stripeInvoices: StripeInvoice[]
}

const defaults = () => {
    return {
        selectedStripeInvoice: null,
        stripeCustomer: null,
        stripeInvoices: [],
        stripePublicKey: null,
    }
}

/* global window.axios events */
export default {
    state: defaults(),
    mutations: {
        selectedStripeInvoiceSet (state: State, invoice: StripeInvoice): void {
            state.selectedStripeInvoice = invoice
        },
        stripeCustomerSet (state: State, stripe: StripeCustomer): void {
            state.stripeCustomer = stripe
        },
        stripeInvoicesPush (state: State, invoices: StripeInvoice[]): void {
            state.stripeInvoices = window.removeDuplicatesById(state.stripeInvoices, invoices)
        },
        stripeReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        stripeCustomerFind ({ commit }, flag: boolean): Promise<StripeCustomer> {
            //  Assemble the API endpoint
            const url = 'billing/customer'

            //  Send the request
            return window.axios.get(url).then(response => {
                //  If the flag is true then we want to commit this customer to the store
                if (flag) commit('stripeCustomerSet',response.data)

                //  Return our customer
                return response.data
            }).catch(() => null)
        },
        stripeCustomerGet ({ commit, getters }): Promise<StripeCustomer> {
            //  Assemble hte API endpoint
            const url = `${getters.api}/billing/customer`

            //  send the request
            return window.axios.get(url).then(response => {
                //  Commit this customer in the store
                commit('stripeCustomerSet', response.data)

                //  Return our stripe customer we found
                return response.data
            }).catch(() => null)
        },
        stripeCustomerPost ({ commit }, obj: StripeCustomer): Promise<StripeCustomer> {
            //  Assemble the API endpoint
            const url = '/billing/customer'

            //  Send the request
            return window.axios.post(url, obj).then(response => {
                //  Set our newly created customer
                commit('stripeCustomerSet', response.data)

                //  Return the new customer
                return response.data
            }).catch(() => null)
        },
        stripeCustomerPut ({ commit }, obj: StripeCustomer): Promise<StripeCustomer> {
            //  Assemble the API endpoint
            const url = '/billing/customer'

            //  send the request
            return window.axios.put(url, obj).then(response => {
                //  Set the updated customer
                commit('stripeCustomerSet', response.data)

                //  Return our updated customer
                return response.data
            }).catch(() => null)
        },
        stripeInvoicesPaginate ({ commit }, params: object): Promise<StripeInvoice[]> {
            //  Assemble the API endpoint
            const url = `/billing/invoices?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our invoices in our store
                commit('stripeInvoicesPush', response.data.data)

                //  REturn our stripe invoices
                return response.data
            }).catch(() => null)
        },
        stripeInvoiceFind ({ commit }, params: object): Promise<StripeInvoice> {
            //  Assemble the aPI endpoint
            const url = `/billing/invoice?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our found stripe invoice in the store
                commit('selectedStripeInvoiceSet', response.data)

                //  Return the found stripe invoice
                return response.data
            }).catch(() => null)
        },
        stripePaymentRemove ({ commit }, id: string): Promise<StripeCustomer> {
            //  Assemble the API endpoint
            const url = `/billing/payment/${id}`

            //  Send our request
            return window.axios.delete(url).then(response => {
                //  Set our updated customer in the store
                commit('stripeCustomerSet', response.data)

                //  Return our updated customer
                return response.data
            }).catch(() => null)
        },
        stripePaymentRetry ({ getters }): Promise<boolean> {
            //  Assemble the API endpoint
            const url = `${getters.api}/billing/retry-payment`

            //  Send our request
            return window.axios.post(url).then(response => {
                //  Return true since it worked
                return true
            }).catch(() => false)
        }
    },
    getters: {
        selectedStripeInvoice (state: State): StripeInvoice {
            return state.selectedStripeInvoice
        },
        stripeCustomer (state: State): StripeCustomer {
            return state.stripeCustomer
        },
        stripeCustomerHasPayment (state: State): boolean {
            //  Grab our stripe customer
            const customer = state.stripeCustomer
            
            //  If we do NOT have a customer, return false
            if (!customer) return false

            //  Else, check if they have ANY payment methods
            const numOfSources = window.getProps(customer, ['sources', 'data', 'length']) || 0

            //  If the number of sources is greater than 0, return true
            return numOfSources > 0
        },
        stripeInvoices (state: State): StripeInvoice[] {
            return state.stripeInvoices
        },
        usedMetered (state: State): number {
            if (!state.selectedStripeInvoice) return 0

            if (!state.selectedStripeInvoice.lines) return 0

            if (!state.selectedStripeInvoice.lines.data) return 0

            //  We can get the actual metered usage by looking at the upcoming stripe invoice
            const meteredLine = state.selectedStripeInvoice.lines.data.find(
                (x) => x.plan && x.plan.usage_type && x.plan.usage_type === 'metered'
            )

            //  If we don't have a metered subscription on this, return 0
            if (!meteredLine) return 0

            //  Else, find the usage
            const usage = meteredLine.quantity

            //  NOTE: This usage right now is inaccurate and requires another endpoint so we can get the selected usage.
            //  Reasoning that this is inaccurate is that if a billing threshold is met, it will reset the usage.
            //  There has to be a better way to figure out this total usage, this only becomes an issue above 2000 msgs

            //  Your total credits has to be 0 to calculate this
            return usage
        }
    }
}
